// Pestaña "Lista de compra" de la ficha del presupuesto (motor nuevo).
//
// Va en paralelo al módulo de Compras antiguo: lo calcula el backend
// (necesidades_evento.py, lista_compra_v2_routes.py) y aquí se muestra, se
// guarda el seguimiento de cada línea (estado y nota) y se pide al proveedor.
//
// - Multievento: selector de jornadas con "Todo el evento" (los paquetes se
//   redondean sobre el ámbito elegido; se avisa si dos jornadas comparten paquete).
// - Agrupación por proveedor o por sección.
// - Tabla compacta (components/compras/TablaListaCompra.js, compartida con la
//   compra de la semana): una fila por ingrediente con necesario, falta, coste
//   de compra por paquetes enteros, estado y nota; el nombre abre la ficha del
//   ingrediente; total por proveedor y de la lista.
// - "No pedir" por línea (tachada, fuera de totales, PDF y WhatsApp) y
//   paquetes editables (se guardan en el servidor y recalculan coste y totales).
// - Pedir a cada proveedor: "PDF del pedido" y "WhatsApp del pedido" con las
//   cantidades vigentes y la fecha de entrega; después, marcar sus líneas como
//   pedidas (sin orden). La orden de compra queda oculta (`MOSTRAR_CREAR_ORDEN`).
// - Exportar: PDF (todo o un proveedor) y texto de WhatsApp para copiar.
// - Comparativa (plegada): motor nuevo frente a la orden de cocina y a Compras
//   antiguo, con el motivo de cada diferencia y qué versión es la correcta.
// Funciona también en borrador (previsión).
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { listaCompraV2API } from '../../api/api';
import { useToast } from '../../hooks/use-toast';
import useEsMovil from '../../hooks/useEsMovil';
import { hoyMadrid } from '../../utils/fechas';
import SelectorJornadas from './SelectorJornadas';
import ComparativaOrdenCocinaNueva from './ComparativaOrdenCocinaNueva';
import TablaListaCompra from '../compras/TablaListaCompra';
import ConfirmarPedido from '../compras/ConfirmarPedido';
import IngredienteModal from '../ingredientes/IngredienteModal';
import {
  MOSTRAR_CREAR_ORDEN, TEXTO_ESTADO, copiarTexto, descargarRespuesta, formatCantidad, formatEuros, vispera,
} from '../compras/listaCompraUtils';

const claseBoton = 'h-11 md:h-[34px] px-3 rounded-lg border text-sm md:text-[13px] font-bold whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed';
const botonPrincipal = `${claseBoton} bg-duetto-primary border-duetto-primary text-white hover:bg-opacity-90`;
const botonSecundario = `${claseBoton} bg-white border-[#CBD3CF] text-[#16221D] hover:bg-gray-50`;
const claseTh = 'px-3 py-2 text-xs font-bold tracking-wide uppercase text-[#5B6B64] border-b border-[#E1E6E3]';
const claseTd = 'px-3 py-2.5 text-sm border-b border-[#EEF1EF] align-top';
const claseSelect = 'h-11 md:h-[32px] px-2 text-base md:text-sm border border-[#CBD3CF] rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-duetto-primary';

const TEXTO_MODO = {
  sin_alcohol: 'Variado sin alcohol',
  con_alcohol: 'Variado con alcohol',
  personalizado: 'Personalizado',
};
const TEXTO_FUENTE = { orden: 'Orden de cocina', antiguo: 'Compras antiguo' };
const TEXTO_CORRECTA = { nuevo: 'Motor nuevo', revisar: 'Revisar', coinciden: 'Coinciden' };

const PestanaListaCompra = ({ presupuestoId, presupuesto }) => {
  const { toast } = useToast();
  // Móvil (< 768 px): la comparativa va en tarjetas en vez de en tabla
  const esMovil = useEsMovil();
  const [datos, setDatos] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);
  const [jornadaId, setJornadaId] = useState(null);
  const [agrupacion, setAgrupacion] = useState('proveedor');
  const [guardando, setGuardando] = useState({}); // {clave: true}
  const [proveedorExport, setProveedorExport] = useState('');
  const [ocupado, setOcupado] = useState(false);
  const [comparativaAbierta, setComparativaAbierta] = useState(false);
  const [comparativa, setComparativa] = useState(null);
  const [cargandoComparativa, setCargandoComparativa] = useState(false);
  const [soloDiferencias, setSoloDiferencias] = useState(true);
  const [pedido, setPedido] = useState(null); // {grupo, creando, resultado}
  const [ingredienteAbierto, setIngredienteAbierto] = useState(null); // id del ingrediente con la ficha abierta

  // Lo que cambia la lista: si cambia, se vuelve a pedir al backend
  const firma = JSON.stringify([
    presupuesto?.num_personas,
    presupuesto?.bebidas_modo,
    presupuesto?.estado,
    (presupuesto?.menus_personalizados || []).map(m => [m.menu_id, m.num_personas, m.platos_ids, m.unidades_por_plato, (m.adaptaciones || []).length]),
    (presupuesto?.jornadas || []).map(j => [j.id, j.num_personas, (j.menus_personalizados || []).map(m => [m.menu_id, m.num_personas, m.platos_ids, m.unidades_por_plato])]),
  ]);

  const cargar = useCallback(async () => {
    if (!presupuestoId) return;
    setCargando(true);
    try {
      const { data } = await listaCompraV2API.get(presupuestoId, jornadaId);
      if (!data || !Array.isArray(data.lineas)) {
        setError('No se pudo cargar la lista de compra.');
        setDatos(null);
        return;
      }
      setError(null);
      setDatos(data);
    } catch (err) {
      setError(err?.response?.data?.detail || 'No se pudo cargar la lista de compra.');
    } finally {
      setCargando(false);
    }
  }, [presupuestoId, jornadaId]);

  useEffect(() => {
    cargar();
  }, [cargar, firma]);

  const cargarComparativa = useCallback(async () => {
    if (!presupuestoId) return;
    setCargandoComparativa(true);
    try {
      const { data } = await listaCompraV2API.comparativa(presupuestoId, jornadaId);
      setComparativa(data);
    } catch (err) {
      setComparativa(null);
      toast({
        title: 'No se pudo cargar la comparativa',
        description: err?.response?.data?.detail || err.message,
        variant: 'destructive',
      });
    } finally {
      setCargandoComparativa(false);
    }
  }, [presupuestoId, jornadaId, toast]);

  // La comparativa se recalcula al cambiar de jornada si está abierta
  useEffect(() => {
    if (comparativaAbierta) cargarComparativa();
  }, [comparativaAbierta, cargarComparativa]);

  // Guarda estado y nota de una línea (el comprador se conserva tal cual)
  const guardarLinea = async (linea, cambios) => {
    setGuardando(prev => ({ ...prev, [linea.clave]: true }));
    try {
      const { data } = await listaCompraV2API.guardarLinea(presupuestoId, linea.ingrediente_id, {
        estado: linea.estado,
        comprador: linea.comprador || '',
        nota: linea.nota || '',
        ...cambios,
        jornada_id: jornadaId,
      });
      setDatos(prev => (prev ? {
        ...prev,
        lineas: prev.lineas.map(l => (l.clave === linea.clave ? {
          ...l,
          estado: data.estado,
          comprador: data.comprador,
          nota: data.nota,
          estado_guardado: true,
          pedible: !l.no_pedir && (l.manual ? Number(l.cantidad_compra) > 0 : Number(l.falta_comprar) > 0) && data.estado === 'pendiente',
          actualizado_por: data.actualizado_por,
          actualizado_en: data.actualizado_en,
        } : l)),
      } : prev));
      // Cambio de estado: se recarga para traer la foto de lo pedido y la falta
      // sobre lo pedido (necesidad cubierta), como en la compra de la semana
      if (cambios.estado !== undefined && cambios.estado !== linea.estado) await cargar();
    } catch (err) {
      toast({
        title: 'No se pudo guardar la línea',
        description: err?.response?.data?.detail || err.message,
        variant: 'destructive',
      });
    } finally {
      setGuardando(prev => {
        const r = { ...prev };
        delete r[linea.clave];
        return r;
      });
    }
  };

  // "No pedir" y cantidades a mano: se guarda solo ese campo y se recarga (el
  // servidor recalcula coste y totales)
  const guardarAjuste = async (linea, cambios) => {
    setGuardando(prev => ({ ...prev, [linea.clave]: true }));
    try {
      await listaCompraV2API.guardarLinea(presupuestoId, linea.ingrediente_id, { ...cambios, jornada_id: jornadaId });
      await cargar();
    } catch (err) {
      toast({
        title: 'No se pudo guardar la línea',
        description: err?.response?.data?.detail || err.message,
        variant: 'destructive',
      });
    } finally {
      setGuardando(prev => {
        const r = { ...prev };
        delete r[linea.clave];
        return r;
      });
    }
  };

  // PDF y WhatsApp del pedido de un proveedor (cantidades vigentes, sin "No pedir")
  const pdfProveedor = async (grupo, fecha) => {
    try {
      const respuesta = await listaCompraV2API.descargarPdf(presupuestoId, jornadaId, grupo.proveedorId, fecha);
      const nombre = String(grupo.titulo || 'proveedor').toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');
      descargarRespuesta(respuesta, `pedido_${presupuesto?.numero || String(presupuestoId).slice(0, 8)}_${nombre}`);
      return true;
    } catch (err) {
      toast({ title: 'No se pudo descargar el PDF', description: err?.response?.data?.detail || err.message, variant: 'destructive' });
      return false;
    }
  };

  const whatsappProveedor = async (grupo, fecha) => {
    try {
      const { data } = await listaCompraV2API.whatsapp(presupuestoId, jornadaId, grupo.proveedorId, fecha);
      if (!data?.texto) {
        toast({ title: 'Nada que pedir', description: `No hay líneas que pedir a ${grupo.titulo}.` });
        return false;
      }
      const ok = await copiarTexto(data.texto);
      toast(ok
        ? { title: 'Texto copiado', description: `Pedido a ${grupo.titulo}: pégalo en WhatsApp.` }
        : { title: 'No se pudo copiar', description: 'Tu navegador no deja copiar al portapapeles.', variant: 'destructive' });
      return ok;
    } catch (err) {
      toast({ title: 'No se pudo preparar el texto', description: err?.response?.data?.detail || err.message, variant: 'destructive' });
      return false;
    }
  };

  const marcarPedidas = async (grupo, fecha = null) => {
    try {
      const { data } = await listaCompraV2API.marcarPedidas(presupuestoId, { jornada_id: jornadaId, proveedor_id: grupo.proveedorId, ...(fecha ? { fecha_entrega: fecha } : {}) });
      toast({ title: 'Líneas marcadas', description: data?.message || 'Marcadas como pedidas.' });
      await cargar();
    } catch (err) {
      toast({ title: 'No se pudieron marcar', description: err?.response?.data?.detail || err.message, variant: 'destructive' });
    }
  };

  const descargarPdf = async () => {
    setOcupado(true);
    try {
      const respuesta = await listaCompraV2API.descargarPdf(presupuestoId, jornadaId, proveedorExport || null);
      descargarRespuesta(respuesta, `lista_compra_${presupuesto?.numero || String(presupuestoId).slice(0, 8)}`);
    } catch (err) {
      toast({ title: 'No se pudo descargar el PDF', description: err.message, variant: 'destructive' });
    } finally {
      setOcupado(false);
    }
  };

  const copiarWhatsapp = async () => {
    setOcupado(true);
    try {
      const { data } = await listaCompraV2API.whatsapp(presupuestoId, jornadaId, proveedorExport || null);
      if (!data?.texto) {
        toast({ title: 'Nada que pedir', description: 'No hay líneas con falta de compra.' });
        return;
      }
      const ok = await copiarTexto(data.texto);
      toast(ok
        ? { title: 'Texto copiado', description: `${(data.mensajes || []).length} ${(data.mensajes || []).length === 1 ? 'proveedor' : 'proveedores'}: pégalo en WhatsApp.` }
        : { title: 'No se pudo copiar', description: 'Tu navegador no deja copiar al portapapeles.', variant: 'destructive' });
    } catch (err) {
      toast({ title: 'No se pudo preparar el texto', description: err?.response?.data?.detail || err.message, variant: 'destructive' });
    } finally {
      setOcupado(false);
    }
  };

  // Fecha de entrega por defecto: la propuesta según el proveedor (días de
  // entrega y antelación) o, si no viene, la víspera de la jornada (o de la
  // primera del evento)
  const entregaPedido = pedido?.grupo?.proveedorId ? (datos?.totales_proveedor?.[pedido.grupo.proveedorId] || null) : null;
  const fechaEntregaInicial = useMemo(() => {
    if (entregaPedido?.fecha_propuesta) return entregaPedido.fecha_propuesta;
    const jornadas = datos?.jornadas || [];
    let fechas;
    if (jornadaId) fechas = jornadas.filter(j => j.id === jornadaId).map(j => j.fecha);
    else if (jornadas.length > 0) fechas = jornadas.map(j => j.fecha);
    else fechas = [presupuesto?.fecha_evento];
    return vispera(fechas, hoyMadrid());
  }, [datos, jornadaId, presupuesto?.fecha_evento, entregaPedido]);

  const crearOrden = async (fecha, entregas = null) => {
    if (!pedido?.grupo?.proveedorId) return;
    setPedido(prev => ({ ...prev, creando: true }));
    try {
      const { data } = await listaCompraV2API.crearOrden(presupuestoId, pedido.grupo.proveedorId, {
        jornada_id: jornadaId,
        fecha_entrega: fecha,
        solo_falta: true,
        ...(entregas ? { entregas } : {}),
      });
      setPedido(prev => ({ ...prev, creando: false, resultado: data }));
      cargar();
    } catch (err) {
      setPedido(prev => ({ ...prev, creando: false }));
      toast({ title: 'No se pudo crear la orden', description: err?.response?.data?.detail || err.message, variant: 'destructive' });
    }
  };

  const lineas = useMemo(() => datos?.lineas || [], [datos]);

  const proveedores = useMemo(() => {
    const mapa = new Map();
    lineas.forEach((l) => {
      if (l.proveedor_id && !mapa.has(l.proveedor_id)) mapa.set(l.proveedor_id, l.proveedor_nombre);
    });
    return Array.from(mapa.entries()).sort((a, b) => String(a[1]).localeCompare(String(b[1]), 'es'));
  }, [lineas]);

  if (cargando && !datos) return <p className="text-sm text-[#5B6B64]">Calculando la lista de compra...</p>;
  if (!datos) return <p className="text-sm text-[#8A1C12]" data-testid="lista-compra-error">{error}</p>;

  const totales = datos.totales || {};
  const avisosPorTipo = totales.avisos || {};
  // Avisos del motor: los de la barra libre del lunch van aparte (no son un
  // problema, solo explican que la bebida del lunch cubierta no se compra)
  const avisosBarraLibre = (datos?.avisos || []).filter(a => a.tipo === 'barra_libre_lunch');
  if (avisosBarraLibre.length === 0 && Number(datos?.barra_libre_lunch?.personas_cubiertas) > 0) {
    avisosBarraLibre.push({
      tipo: 'barra_libre_lunch',
      texto: `Barra libre del lunch: no se cuentan ${datos.barra_libre_lunch.personas_cubiertas} bebidas del lunch.`,
    });
  }
  const avisosGenerales = (datos?.avisos || []).filter(a => a.tipo !== 'barra_libre_lunch');
  const totalCoste = datos.total_coste !== undefined ? datos.total_coste : totales.coste_estimado;

  const renderComparativa = () => {
    if (cargandoComparativa && !comparativa) return <p className="text-sm text-[#5B6B64]">Comparando con la orden de cocina y Compras...</p>;
    if (!comparativa) return <p className="text-sm text-[#5B6B64]">Sin datos de comparativa.</p>;
    const fuentes = ['orden', 'antiguo'];
    const filas = (comparativa.lineas || []).filter(f => !soloDiferencias || fuentes.some(fu => f[fu] !== null && f[fu] !== undefined && !f[`cuadra_${fu}`]));
    return (
      <div className="flex flex-col gap-3" data-testid="lista-compra-comparativa">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {fuentes.map((fu) => {
            const r = comparativa.resumen?.[fu] || {};
            const f = comparativa.fuentes?.[fu] || {};
            return (
              <div key={fu} className="rounded-lg border border-[#E1E6E3] px-3 py-2.5 text-sm">
                <div className="font-bold text-[#16221D]">{TEXTO_FUENTE[fu]}</div>
                {!f.disponible ? (
                  <div className="text-xs text-[#8A1C12]">No disponible{f.error ? `: ${f.error}` : ''}</div>
                ) : (
                  <div className="text-xs text-[#4A5A53]">
                    {r.cuadran || 0} cuadran · {r.difieren || 0} difieren ({r.explicadas || 0} explicadas)
                    {Object.keys(r.correcta || {}).length > 0 && (
                      <div>
                        Correcta: {Object.entries(r.correcta).map(([k, v]) => `${TEXTO_CORRECTA[k] || k} ${v}`).join(' · ')}
                      </div>
                    )}
                  </div>
                )}
                {f.nota && <div className="text-xs text-[#92400E] mt-0.5">{f.nota}</div>}
              </div>
            );
          })}
        </div>
        <label className="flex items-center gap-2 min-h-[40px] md:min-h-0 text-[13px] md:text-xs text-[#4A5A53]">
          <input type="checkbox" className="w-5 h-5 md:w-auto md:h-auto" checked={soloDiferencias} onChange={(e) => setSoloDiferencias(e.target.checked)} />
          Solo las diferencias
        </label>
        {filas.length === 0 ? (
          <p className="text-sm text-[#0F5E43]">Todo cuadra.</p>
        ) : esMovil ? (
          // Móvil: una tarjeta por ingrediente (nuevo, orden y antiguo en una fila; motivos debajo)
          <ul className="flex flex-col gap-2" data-testid="lista-compra-comparativa-movil">
            {filas.map(f => (
              <li key={f.ingrediente_id} className="rounded-lg border border-[#E1E6E3] px-3 py-2.5 text-sm flex flex-col gap-1.5">
                <div className="flex items-start justify-between gap-3">
                  <span className="min-w-0 font-semibold text-[#16221D] break-words">{f.nombre}</span>
                  <span className="text-xs text-[#5B6B64] whitespace-nowrap">{f.unidad}</span>
                </div>
                {(f.avisos || []).map(a => (
                  <div key={a.tipo} className="text-xs text-[#92400E] font-semibold">{a.texto}</div>
                ))}
                <div className="grid grid-cols-3 gap-1.5 text-center">
                  <div className="rounded-md bg-[#F7F9F8] px-1 py-1">
                    <div className="text-xs text-[#5B6B64]">Nuevo</div>
                    <div className="tabular-nums font-bold">{formatCantidad(f.nuevo)}</div>
                  </div>
                  {fuentes.map(fu => (
                    <div key={fu}
                      className={`rounded-md px-1 py-1 ${f[fu] === null || f[fu] === undefined ? 'bg-[#F7F9F8] text-[#9AA6A0]' : (f[`cuadra_${fu}`] ? 'bg-[#F7F9F8] text-[#0F5E43]' : 'bg-[#FDF0DC] text-[#7A3A06] font-bold')}`}>
                      <div className="text-xs text-[#5B6B64] font-normal">{fu === 'orden' ? 'Orden' : 'Antiguo'}</div>
                      <div className="tabular-nums">{f[fu] === null || f[fu] === undefined ? '-' : formatCantidad(f[fu])}</div>
                    </div>
                  ))}
                </div>
                {fuentes.filter(fu => (f[`motivos_${fu}`] || []).length > 0).map(fu => (
                  <div key={fu} className="text-xs">
                    <span className="font-bold">{TEXTO_FUENTE[fu]}: </span>
                    {(f[`motivos_${fu}`] || []).map(m => m.texto).join(' ')}
                    <div className={f[`veredicto_${fu}`]?.correcta === 'nuevo' ? 'text-[#0F5E43]' : 'text-[#92400E]'}>
                      Correcta: {TEXTO_CORRECTA[f[`veredicto_${fu}`]?.correcta] || f[`veredicto_${fu}`]?.correcta}. {f[`veredicto_${fu}`]?.por_que}
                    </div>
                  </div>
                ))}
              </li>
            ))}
          </ul>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[760px] border-collapse">
              <thead>
                <tr>
                  <th className={`${claseTh} text-left`}>Ingrediente</th>
                  <th className={`${claseTh} text-right`}>Nuevo</th>
                  <th className={`${claseTh} text-right`}>Orden</th>
                  <th className={`${claseTh} text-right`}>Antiguo</th>
                  <th className={`${claseTh} text-left`}>Motivo y cuál es correcta</th>
                </tr>
              </thead>
              <tbody>
                {filas.map(f => (
                  <tr key={f.ingrediente_id}>
                    <td className={claseTd}>
                      <div className="font-semibold">{f.nombre}</div>
                      <div className="text-xs text-[#5B6B64]">{f.unidad}</div>
                      {(f.avisos || []).map(a => (
                        <div key={a.tipo} className="text-xs text-[#92400E] font-semibold mt-0.5" title={a.texto}>{a.texto}</div>
                      ))}
                    </td>
                    <td className={`${claseTd} text-right tabular-nums font-bold`}>{formatCantidad(f.nuevo)}</td>
                    {fuentes.map(fu => (
                      <td
                        key={fu}
                        className={`${claseTd} text-right tabular-nums ${f[fu] === null || f[fu] === undefined ? 'text-[#9AA6A0]' : (f[`cuadra_${fu}`] ? 'text-[#0F5E43]' : 'bg-[#FDF0DC] text-[#7A3A06] font-bold')}`}
                      >
                        {f[fu] === null || f[fu] === undefined ? '-' : formatCantidad(f[fu])}
                      </td>
                    ))}
                    <td className={`${claseTd} text-xs`}>
                      {fuentes.filter(fu => (f[`motivos_${fu}`] || []).length > 0).map(fu => (
                        <div key={fu} className="mb-1.5">
                          <span className="font-bold">{TEXTO_FUENTE[fu]}: </span>
                          {(f[`motivos_${fu}`] || []).map(m => m.texto).join(' ')}
                          <div className={f[`veredicto_${fu}`]?.correcta === 'nuevo' ? 'text-[#0F5E43]' : 'text-[#92400E]'}>
                            Correcta: {TEXTO_CORRECTA[f[`veredicto_${fu}`]?.correcta] || f[`veredicto_${fu}`]?.correcta}. {f[`veredicto_${fu}`]?.por_que}
                          </div>
                        </div>
                      ))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="flex flex-col gap-4" data-testid="pestana-lista-compra">
      {datos.multievento && (datos.jornadas || []).length > 0 && (
        <SelectorJornadas
          jornadas={datos.jornadas}
          jornadaId={jornadaId}
          onChange={setJornadaId}
          conTodas
          textoTodas="Todo el evento"
          testid="lista-compra-jornadas"
          prefijoTestid="lista-compra-jornada"
        />
      )}

      <div className="flex flex-wrap items-center justify-between gap-3 rounded-[12px] border border-[#E1E6E3] bg-[#F7F9F8] px-4 py-3">
        <div className="flex flex-col gap-0.5 text-sm">
          <span className="font-extrabold text-[#16221D]">
            {totales.lineas || 0} {totales.lineas === 1 ? 'artículo' : 'artículos'} · {totales.con_falta || 0} por comprar
            {Number(totales.no_pedir) > 0 ? ` · ${totales.no_pedir} sin pedir` : ''}
            {Number(totalCoste) > 0 ? ` · compra estimada ${formatEuros(totalCoste)} sin IVA` : ''}
          </span>
          <span className="text-xs text-[#5B6B64]">
            {/* El modo de bebidas solo se muestra si algún menú lleva el plato de bebida mixto */}
            {datos.bebidas_modo_aplica !== false && <>Bebidas: {TEXTO_MODO[datos.bebidas_modo] || datos.bebidas_modo}</>}
            {datos.multievento ? (datos.bebidas_modo_aplica !== false
              ? ` · ${jornadaId ? 'una jornada' : 'todo el evento (paquetes sobre el total)'}`
              : (jornadaId ? 'Una jornada' : 'Todo el evento (paquetes sobre el total)')) : ''}
          </span>
        </div>
        {datos.prevision && (
          <span className="px-2 py-1 rounded-full text-xs font-bold bg-[#FDF0DC] text-[#92400E]" data-testid="lista-compra-prevision">
            Previsión: presupuesto sin confirmar
          </span>
        )}
      </div>

      {/* Barra libre del lunch: informativo (la bebida del lunch cubierta no se compra) */}
      {avisosBarraLibre.length > 0 && (
        <div className="rounded-[10px] border px-3.5 py-3 text-sm bg-[#E3F3EC] border-[#BFE3D2] text-[#0F5E43]" data-testid="lista-compra-barra-libre">
          {avisosBarraLibre.map(a => <div key={`${a.tipo}-${a.texto}`}>{a.texto}</div>)}
        </div>
      )}

      {(avisosGenerales.length > 0 || Object.keys(avisosPorTipo).length > 0) && (
        <div className="rounded-[10px] border px-3.5 py-3 text-sm bg-[#FDF0DC] border-[#F4D9AE] text-[#7A3A06]" data-testid="lista-compra-avisos">
          {avisosGenerales.map(a => <div key={`${a.tipo}-${a.texto}`}>{a.texto}</div>)}
          {avisosPorTipo.compartido > 0 && <div>{avisosPorTipo.compartido} artículos comparten paquete entre jornadas: se puede hacer una sola compra.</div>}
          {avisosPorTipo.sin_proveedor > 0 && <div>{avisosPorTipo.sin_proveedor} artículos sin proveedor.</div>}
          {avisosPorTipo.sin_unidad_compra > 0 && <div>{avisosPorTipo.sin_unidad_compra} artículos sin paquete definido.</div>}
          {avisosPorTipo.racion_sospechosa > 0 && (
            <div data-testid="lista-compra-aviso-racion">
              {avisosPorTipo.racion_sospechosa === 1
                ? '1 ingrediente con una cantidad por ración sospechosa (menos de 5 g): revisa la ficha del plato.'
                : `${avisosPorTipo.racion_sospechosa} ingredientes con una cantidad por ración sospechosa (menos de 5 g): revisa la ficha del plato.`}
            </div>
          )}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <button type="button" onClick={cargar} disabled={cargando} className={botonSecundario} data-testid="lista-compra-actualizar">
          {cargando ? 'Actualizando...' : 'Actualizar'}
        </button>
        <div className="flex rounded-lg border border-[#CBD3CF] overflow-hidden" role="group" aria-label="Agrupar">
          {[['proveedor', 'Por proveedor'], ['seccion', 'Por sección']].map(([valor, texto]) => (
            <button
              key={valor}
              type="button"
              onClick={() => setAgrupacion(valor)}
              aria-pressed={agrupacion === valor}
              className={`h-11 md:h-[34px] px-3 text-sm md:text-[13px] font-bold ${agrupacion === valor ? 'bg-[#E3F3EC] text-[#0F5E43]' : 'bg-white text-[#16221D] hover:bg-gray-50'}`}
              data-testid={`lista-compra-agrupar-${valor}`}
            >
              {texto}
            </button>
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-2 ml-auto">
          <select
            value={proveedorExport}
            onChange={(e) => setProveedorExport(e.target.value)}
            className={claseSelect}
            aria-label="Proveedor para exportar"
            data-testid="lista-compra-proveedor-export"
          >
            <option value="">Todos los proveedores</option>
            {proveedores.map(([id, nombre]) => <option key={id} value={id}>{nombre}</option>)}
          </select>
          <button type="button" onClick={descargarPdf} disabled={ocupado} className={botonSecundario} data-testid="lista-compra-pdf">PDF</button>
          <button type="button" onClick={copiarWhatsapp} disabled={ocupado} className={botonPrincipal} data-testid="lista-compra-whatsapp">WhatsApp</button>
        </div>
      </div>
      {error && <p className="text-xs text-[#8A1C12]">{error}</p>}

      {lineas.length === 0 ? (
        <p className="text-sm text-[#5B6B64]" data-testid="lista-compra-vacia">Los menús de este presupuesto no llevan ingredientes que comprar.</p>
      ) : (
        <TablaListaCompra
          lineas={lineas}
          secciones={datos.secciones || []}
          estados={datos.estados_disponibles}
          agrupacion={agrupacion}
          multievento={!!datos.multievento}
          guardando={guardando}
          onCambiarEstado={(l, estado) => guardarLinea(l, { estado })}
          onGuardarNota={(l, nota) => guardarLinea(l, { nota })}
          onNoPedir={(l, valor) => guardarAjuste(l, { no_pedir: valor })}
          onCambiarCantidad={guardarAjuste}
          onPdfProveedor={pdfProveedor}
          onWhatsappProveedor={whatsappProveedor}
          onMarcarPedidas={marcarPedidas}
          onPedir={MOSTRAR_CREAR_ORDEN ? (grupo) => setPedido({ grupo, creando: false, resultado: null }) : null}
          onAbrirIngrediente={setIngredienteAbierto}
          totalCoste={totalCoste}
          notaCoste={datos.nota_coste}
          totalesProveedor={datos.totales_proveedor}
          prefijoTestid="lista-compra"
        />
      )}

      {(datos.lineas_ya_no_necesarias || []).length > 0 && (
        <div className="rounded-lg border border-[#E1E6E3] px-3.5 py-3 text-sm" data-testid="lista-compra-ya-no">
          <div className="font-bold text-[#16221D] mb-1">Con seguimiento pero ya no hacen falta</div>
          <ul className="text-xs text-[#5B6B64]">
            {datos.lineas_ya_no_necesarias.map(h => (
              <li key={h.clave} className="line-through">
                {h.nombre || h.ingrediente_id} · {h.no_pedir ? 'No pedir' : (TEXTO_ESTADO[h.estado] || h.estado)}{h.orden_numero ? ` (${h.orden_numero})` : ''}{h.nota ? ` · ${h.nota}` : ''}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="rounded-[12px] border border-[#E1E6E3]">
        <button
          type="button"
          onClick={() => setComparativaAbierta(v => !v)}
          aria-expanded={comparativaAbierta}
          className="w-full flex items-center justify-between gap-3 px-4 py-3 min-h-[48px] md:min-h-0 text-left text-sm font-bold text-[#16221D]"
          data-testid="lista-compra-comparativa-toggle"
        >
          <span>Comparativa con la orden de cocina y Compras antiguo</span>
          <span className="text-xs text-[#5B6B64]">{comparativaAbierta ? 'Ocultar' : 'Mostrar'}</span>
        </button>
        {comparativaAbierta && (
          <div className="px-4 pb-4 flex flex-col gap-2">
            <div className="flex justify-end">
              <button type="button" onClick={cargarComparativa} disabled={cargandoComparativa} className={botonSecundario}>
                {cargandoComparativa ? 'Comparando...' : 'Volver a comparar'}
              </button>
            </div>
            {renderComparativa()}
            <ComparativaOrdenCocinaNueva presupuestoId={presupuestoId} jornadaId={jornadaId} />
          </div>
        )}
      </div>

      <ConfirmarPedido
        abierto={!!pedido}
        grupo={pedido?.grupo}
        fechaInicial={fechaEntregaInicial}
        entrega={entregaPedido}
        creando={!!pedido?.creando}
        resultado={pedido?.resultado}
        onConfirmar={crearOrden}
        onCerrar={() => setPedido(null)}
      />

      <IngredienteModal
        abierto={!!ingredienteAbierto}
        ingredienteId={ingredienteAbierto}
        onCerrar={() => setIngredienteAbierto(null)}
        onGuardado={() => cargar()}
      />
    </div>
  );
};

export default PestanaListaCompra;
